import { ATTRIBUTION_STORAGE_KEY, AttributionData } from "./attribution";
import { safeLocalStorage, safeSessionStorage } from "./safeStorage";

/**
 * Resolve template variables in a URL string.
 * Variables use the $variableName syntax (e.g., $anonymousId, $source, $landingPageId).
 * Values are URI-encoded to prevent injection.
 */
export function resolveUrlTemplate(
  template: string,
  variables: Record<string, string | number | undefined>,
): string {
  return template.replace(/\$(\w+)/g, (_match, key) => {
    const value = variables[key];
    return value !== undefined ? encodeURIComponent(String(value)) : "";
  });
}

/**
 * Generate a short, URL-friendly redirect token (8 alphanumeric chars).
 * Used instead of the full anonymous ID to keep outbound URLs clean.
 * The linkage event stores both the token and the full anonymous ID for Redshift joins.
 * Stable per landing page session (stored in sessionStorage).
 */
export function getRedirectToken(landingPageId: string): string {
  const key = `hm_redirect_token_${landingPageId}`;
  const existing = safeSessionStorage.getItem(key);
  if (existing) return existing;

  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let token = "";
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  for (let i = 0; i < 8; i++) {
    token += chars[array[i] % chars.length];
  }
  safeSessionStorage.setItem(key, token);
  return token;
}

/**
 * Read saved attribution data from localStorage.
 * Returns the full attribution data for use in URL templates and tracking events.
 */
export function getStoredAttribution(): AttributionData {
  const stored = safeLocalStorage.getItem(ATTRIBUTION_STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as AttributionData;
    } catch {
      // corrupt value — return empty
    }
  }
  return {};
}
